import { useState, useEffect, useMemo } from 'react'
import {
  MoreVertical,
  Play,
  Edit,
  Copy,
  Trash2,
  Rocket,
  CheckCircle,
  Clock,
  Archive,
  TestTube,
  Plus,
  RefreshCw,
  Bot,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { Link } from 'react-router-dom'
import { type CustomAgentSummary, type DeploymentStatus } from '@/lib/api/services/aiStudio'
import {
  useCustomAgents,
  useDeleteCustomAgent,
  useRunAgentTests,
  useDeployAgent
} from '@/lib/hooks/use-ai-studio'
import { AgentsTableView, type SortField, type SortOrder } from './AgentsTableView'

type ViewMode = 'grid' | 'table'
type StatusFilter = 'all' | DeploymentStatus
type TypeFilter = 'all' | 'custom' | 'preset'

interface MyAgentsTabProps {
  searchQuery: string
  viewMode: ViewMode
  statusFilter: StatusFilter
  typeFilter: TypeFilter
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onTotalCountChange: (count: number) => void
}

// MS-style agent colors and roles - matches AgentSeeder configuration
const agentConfig: Record<string, { color: string; role: string }> = {
  alex: { color: '#3B82F6', role: 'Finder' },
  scarlet: { color: '#EF4444', role: 'Guardian' },
  sia: { color: '#8B5CF6', role: 'Writer' },
  olga: { color: '#F59E0B', role: 'Strategist' },
  dimi: { color: '#F97316', role: 'Builder' },
  andrew: { color: '#14B8A6', role: 'Analyst' },
}

// Generate 2-letter abbreviation from name
function getAbbreviation(name: string): string {
  if (!name || name.length === 0) return 'AI'
  const words = name.trim().split(/\s+/).filter(w => w.length > 0)
  if (words.length >= 2 && words[0] && words[1]) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

// Get color for agent
function getAgentColor(slug: string, isCustom: boolean): string {
  if (!isCustom && agentConfig[slug]) {
    return agentConfig[slug].color
  }
  const colors = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#8B5CF6'] as const
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length] ?? '#6366F1'
}

// Get role label for display
function getAgentRole(slug: string, agentType: string, isCustom: boolean): string {
  if (!isCustom && agentConfig[slug]) {
    return agentConfig[slug].role
  }
  return agentType
}

const deploymentStatusConfig: Record<DeploymentStatus, { label: string; color: string; icon: React.ElementType }> = {
  Draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: Clock },
  Testing: { label: 'Testing', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: TestTube },
  Active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  Deprecated: { label: 'Deprecated', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Archive },
}

// Status order for sorting
const statusOrder: Record<DeploymentStatus, number> = {
  Active: 1,
  Testing: 2,
  Draft: 3,
  Deprecated: 4,
}

export function MyAgentsTab({
  searchQuery,
  viewMode,
  statusFilter,
  typeFilter,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onTotalCountChange
}: MyAgentsTabProps) {
  const { toast } = useToast()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [agentToDelete, setAgentToDelete] = useState<CustomAgentSummary | null>(null)

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Use React Query hooks for data fetching
  const { data: agents = [], isLoading, error, refetch } = useCustomAgents(true, true)

  const deleteMutation = useDeleteCustomAgent({
    onSuccess: () => {
      toast({
        title: 'Deleted',
        description: `${agentToDelete?.name} has been deleted`,
      })
      setDeleteDialogOpen(false)
      setAgentToDelete(null)
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete agent',
        variant: 'destructive',
      })
    },
  })

  const runTestsMutation = useRunAgentTests({
    onSuccess: (result) => {
      toast({
        title: 'Tests Complete',
        description: `${result.passedTests}/${result.totalTests} tests passed (${result.successRate.toFixed(0)}%)`,
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to run tests',
        variant: 'destructive',
      })
    },
  })

  const deployMutation = useDeployAgent({
    onSuccess: (agent) => {
      toast({
        title: 'Deployed',
        description: `${agent.name} is now active`,
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to deploy agent',
        variant: 'destructive',
      })
    },
  })

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
    onPageChange(1) // Reset to first page when sorting changes
  }

  // Apply filters and sorting
  const filteredAndSortedAgents = useMemo(() => {
    // Filter first
    let result = agents.filter((agent) => {
      const matchesSearch = searchQuery === '' ||
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus = statusFilter === 'all' || agent.deploymentStatus === statusFilter

      const matchesType = typeFilter === 'all' ||
        (typeFilter === 'custom' && agent.isCustom) ||
        (typeFilter === 'preset' && !agent.isCustom)

      return matchesSearch && matchesStatus && matchesType
    })

    // Then sort
    result = [...result].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'agentType':
          comparison = a.agentType.localeCompare(b.agentType)
          break
        case 'deploymentStatus':
          comparison = statusOrder[a.deploymentStatus] - statusOrder[b.deploymentStatus]
          break
        case 'skillCount':
          comparison = a.skillCount - b.skillCount
          break
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })

    return result
  }, [agents, searchQuery, statusFilter, typeFilter, sortField, sortOrder])

  // Update total count when filtered agents change
  useEffect(() => {
    onTotalCountChange(filteredAndSortedAgents.length)
  }, [filteredAndSortedAgents.length, onTotalCountChange])

  // Paginate
  const totalPages = Math.ceil(filteredAndSortedAgents.length / pageSize)
  const startIndex = (page - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedAgents = filteredAndSortedAgents.slice(startIndex, endIndex)

  // Reset page if current page is out of bounds
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      onPageChange(totalPages)
    }
  }, [page, totalPages, onPageChange])

  const handleTest = (agent: CustomAgentSummary) => {
    toast({
      title: 'Running Tests',
      description: `Running tests for ${agent.name}...`,
    })
    runTestsMutation.mutate(agent.id)
  }

  const handleDeploy = (agent: CustomAgentSummary) => {
    deployMutation.mutate(agent.id)
  }

  const handleDelete = () => {
    if (!agentToDelete) return
    deleteMutation.mutate(agentToDelete.id)
  }

  const handleDeleteClick = (agent: CustomAgentSummary) => {
    setAgentToDelete(agent)
    setDeleteDialogOpen(true)
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <Bot className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Failed to load agents
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {error.message || 'An error occurred while loading agents'}
        </p>
        <Button onClick={() => refetch()} className="rounded-full">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    )
  }

  if (filteredAndSortedAgents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="p-4 rounded-full bg-purple-100 dark:bg-purple-900/30 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <Bot className="h-8 w-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {searchQuery || statusFilter !== 'all' || typeFilter !== 'all' ? 'No agents found' : 'No agents available'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
            ? 'Try adjusting your filters or search query'
            : 'Create your first custom AI agent to get started'}
        </p>
        {!searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
          <Link to="/ai-studio/builder">
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white">
              <Plus className="mr-2 h-4 w-4" />
              Create Agent
            </Button>
          </Link>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {paginatedAgents.map((agent) => {
            const statusConfig = deploymentStatusConfig[agent.deploymentStatus]
            const StatusIcon = statusConfig.icon
            const agentColor = getAgentColor(agent.slug, agent.isCustom)
            const abbreviation = getAbbreviation(agent.name)
            const role = getAgentRole(agent.slug, agent.agentType, agent.isCustom)

            return (
              <div
                key={agent.id}
                className="group relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-semibold text-base shadow-sm"
                      style={{ backgroundColor: agentColor }}
                    >
                      {abbreviation}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {agent.name}
                        <span className="text-gray-400 font-normal">, {role}</span>
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{agent.agentType}</p>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to={`/ai-studio/builder/${agent.id}`}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleTest(agent)}>
                        <TestTube className="mr-2 h-4 w-4" />
                        Run Tests
                      </DropdownMenuItem>
                      {agent.deploymentStatus !== 'Active' && (
                        <DropdownMenuItem onClick={() => handleDeploy(agent)}>
                          <Rocket className="mr-2 h-4 w-4" />
                          Deploy
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem>
                        <Copy className="mr-2 h-4 w-4" />
                        Clone
                      </DropdownMenuItem>
                      {agent.isCustom && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteClick(agent)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                  {agent.description || 'No description'}
                </p>

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Badge className={`${statusConfig.color} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {agent.isCustom ? (
                    <Badge className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      Custom
                    </Badge>
                  ) : (
                    <Badge className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                      Preset
                    </Badge>
                  )}
                  {agent.skillCount > 0 && (
                    <Badge
                      className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    >
                      {agent.skillCount} {agent.skillCount === 1 ? 'skill' : 'skills'}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Link to={`/ai-studio/chat/${agent.slug}`} className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full rounded-xl text-purple-600 border-purple-200 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-400 dark:hover:bg-purple-900/20"
                    >
                      <Play className="mr-2 h-3 w-3" />
                      Try Agent
                    </Button>
                  </Link>
                  <Link to={`/ai-studio/builder/${agent.id}`}>
                    <Button variant="ghost" size="sm" className="rounded-xl">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <AgentsTableView
          agents={paginatedAgents}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          onTest={handleTest}
          onDeploy={handleDeploy}
          onDelete={handleDeleteClick}
        />
      )}

      {/* Pagination - always show when there are items */}
      {filteredAndSortedAgents.length > 0 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedAgents.length)} of {filteredAndSortedAgents.length} agents
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Per page:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  onPageSizeChange(Number(value))
                  onPageChange(1)
                }}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-300 px-3">
                Page {page} of {Math.max(1, totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{agentToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
